<script setup lang="ts" generic="T">
import { ChevronRightIcon } from '@blpde/nx-icons';
import {
  computed,
  Fragment,
  provide,
  ref,
  useAttrs,
  useSlots,
  type Component,
  type VNode,
} from 'vue';
import { type BreadcrumbItemProps, registerBreadcrumbItemKey } from './internal';
import NxBreadcrumbItem from './NxBreadcrumbItem.vue';

const {
  customLink = undefined,
  size = 'lg',
  onSelect = undefined,
} = defineProps<{
  /** A custom link Component, e.g. RouterLink from vue-router. */
  customLink?: Component<{ $props: { to: string extends T ? T : never } }>;
  /** Visual size. `'lg'` (default) is the large, brand-accented breadcrumb;
   *  `'md'` and `'sm'` are progressively smaller, neutral (non-brand) labels. */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Called with a crumb's `href` instead of following it (click or Enter) —
   * for a path that is no route, like the folders of a file dialog. The hrefs
   * still order the crumbs (shortest first) and must be unique.
   */
  onSelect?: (href: string) => void;
}>();
defineOptions({ inheritAttrs: false });

/**
 * Registered slot bodies, keyed by href. Children push their props + render
 * slot here on mount and remove it on unmount — this is the source of truth
 * for "what to render inside each crumb".
 *
 * Order is NOT taken from this array — see `breadcrumbs` below.
 */
const registered = ref<{ props: BreadcrumbItemProps; item: Component }[]>([]);
function registerBreadcrumbItem(props: BreadcrumbItemProps, item: Component) {
  if (registered.value.find((v) => v.props.href === props.href)) {
    throw Error(
      `Breadcrumb with href '${props.href}' already registered. The href must be unique!`
    );
  }
  registered.value.push({ props, item });
  return () => {
    registered.value = registered.value.filter((v) => v.props.href !== props.href);
  };
}
provide(registerBreadcrumbItemKey, registerBreadcrumbItem);

/**
 * Order is read from the slot VNode tree, not from registration mount order.
 *
 * The register-on-mount pattern always appends to the end, which means
 * Vue's v-for diff (reuse on matching key, unmount old, mount new at end)
 * leaves `registered` in mount order — not in slot template order. When a
 * route change swaps some crumbs and reuses others, the result can be
 * visually out-of-order even though the consumer's v-for is correct.
 *
 * Solution: walk the default slot's VNodes each render, in declaration
 * order, and look up each href in `registered` to get the slot body to
 * render. Order comes from the slot tree; content comes from register.
 */
const slots = useSlots();
const attrs = useAttrs();

function flatten(nodes: readonly VNode[]): VNode[] {
  const out: VNode[] = [];
  for (const n of nodes) {
    if (n.type === Fragment && Array.isArray(n.children)) {
      out.push(...flatten(n.children as VNode[]));
    } else {
      out.push(n);
    }
  }
  return out;
}

const breadcrumbs = computed(() => {
  const orderedHrefs = flatten(slots.default?.() ?? [])
    .filter((v) => v.type === NxBreadcrumbItem)
    .map((v) => (v.props as BreadcrumbItemProps | null)?.href)
    .filter((h): h is string => typeof h === 'string')
    .sort((a, b) => a.length - b.length);
  return orderedHrefs
    .map((href) => registered.value.find((r) => r.props.href === href))
    .filter((r): r is NonNullable<typeof r> => r != null);
});
</script>

<template>
  <!-- children -->
  <slot></slot>

  <nav
    v-bind="attrs"
    :class="['breadcrumb-list', { 'size-md': size === 'md', 'size-sm': size === 'sm' }]"
  >
    <template v-for="(crumb, index) in breadcrumbs" :key="crumb.props.href">
      <div
        :class="['breadcrumb-item', { active: index === breadcrumbs.length - 1 }]"
        :tabindex="1"
        @keydown.enter="onSelect?.(crumb.props.href)"
      >
        <component :is="customLink" v-if="customLink" :to="crumb.props.href ?? ''" :tabindex="-1">
          <component :is="crumb.item"></component>
        </component>
        <a
          v-else
          :href="crumb.props.href ?? ''"
          :tabindex="-1"
          @click="onSelect && ($event.preventDefault(), onSelect(crumb.props.href))"
        >
          <component :is="crumb.item"></component>
        </a>
      </div>
      <ChevronRightIcon v-if="index < breadcrumbs.length - 1" filled />
    </template>
  </nav>
</template>

<style scoped>
.breadcrumb-list {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
  width: fit-content;
  font-size: var(--nxt-font-size-xl);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-breadcrumb-fg);
}
.breadcrumb-item {
  display: inline-flex;
  text-wrap: nowrap;
  text-decoration: none;
  &:hover,
  &:focus-visible {
    color: var(--nxt-breadcrumb-fg-hover);
    outline: none;
  }
  &.active {
    color: var(--nxt-breadcrumb-fg-current);
  }
  & a {
    text-decoration: inherit;
    color: inherit;
  }
}

/* Compact, neutral variants (md < default lg; sm < md): smaller text and no
   brand accent on the active crumb, so the breadcrumb reads as quiet chrome. */
.breadcrumb-list.size-md {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}
.breadcrumb-list.size-sm {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
}
</style>
